import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  DropdownBody,
  DropdownItem,
  Icons,
  Modal,
  useToast,
} from "fds";
import "./index.scss";
import { MenuListType } from "../../types/files";
import utilsCommon from "../../shared/utils/common";
import { meetingDTO } from "../../types/meeting";
import ShareMeetingLinkModal from "../More/ShareMeetingLinkModal";
import useMeetingStore from "../../redux/dispatcher/useMeetingStore";
import { startZoomMeeting } from "../../shared/utils/api";
import { t } from "i18next";
import { nanoid } from "@reduxjs/toolkit";
import MeetingDetailModal from "../MeetingDetailModal";
import AppRegisterModal from "../AppRegisterModal";
import { useNavigate } from "react-router-dom";
import useAppStore from "../../redux/dispatcher/useAppStore";
const copy = require("clipboard-copy");

interface MeetingSettingButtonProps {
  meetingSettingInfo: {
    x: number;
    y: number;
    id: string;
    meetingInfo?: meetingDTO;
  };
  changeEditMode?: (meetingId: string) => void;
  setMeetingSettingInfo: (req: {
    x: number;
    y: number;
    id: string;
    meetingInfo?: meetingDTO;
  }) => void;
  page?: "home";
  handleRefreshMeetingList?: () => void; // 화상회의 삭제 후 동작
}
const MeetingSettingButton: React.FC<MeetingSettingButtonProps> = ({
  meetingSettingInfo,
  page,
  setMeetingSettingInfo,
  changeEditMode,
  handleRefreshMeetingList,
}) => {
  const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'

  /** Hook 정리 */
  const { deleteMeeting } = useMeetingStore();
  const { appState } = useAppStore();
  const toast = useToast();
  const inputRef = useRef<HTMLTableRowElement>(null);
  const navigate = useNavigate();
  const toasts = useToast();

  /** State 정리 */
  const [height, setHeight] = useState(0);
  const [extranModalType, changeExtraModalType] = useState("CLOSE");

  /** 더보기 가능 메뉴 리스트 */
  let menus = [
    {
      key: "startMeeting",
      resource: t("meeting.action.start"),
      icon: <Icons variant="line" label="play" />,
      onClick: () => {
        startZoomMeeting(meetingSettingInfo.meetingInfo, toast);
        handleTurnOffSetting();
      },
    },
    {
      key: "editMeeting",
      resource: t("common.edit"),
      icon: <Icons variant="line" label="edit2" />,
      onClick: () => {
        !appState.appRegisterStatus.zoom
          ? changeExtraModalType("REGISTER_ZOOM@EDIT")
          : handleMoveToEditPage();
      },
    },
    {
      key: "meetingDetail",
      resource: t("file.detail"),
      icon: <Icons variant="line" label="info" />,
      onClick: () => {
        changeExtraModalType("MEETING_DETAIL");
      },
    },
    {
      key: "copyLinkMeeting",
      resource: t("meeting.action.copyInvite"),
      icon: <Icons variant="line" label="link" />,
      onClick: () => {
        if (meetingSettingInfo.meetingInfo?.joinUrl) {
          copy(meetingSettingInfo.meetingInfo?.joinUrl);
          toast.toastMsg(
            nanoid(),
            t("file.aiChat.alert.copySuccess"),
            "success"
          );
          handleTurnOffSetting();
        }
      },
    },
    {
      key: "linkMeeting",
      resource: t("meeting.action.invite"),
      icon: <Icons variant="line" label="link" />,
      onClick: () => {
        changeExtraModalType("SHARE_LINK");
      },
    },
    {
      key: "enterMeeting",
      resource: t("meeting.action.join"),
      icon: <Icons variant="line" label="zoom" />,
      onClick: () => {
        startZoomMeeting(meetingSettingInfo.meetingInfo, toast);
        handleTurnOffSetting();
      },
    },

    {
      key: "deleteMeeting",
      resource: t("common.delete"),
      icon: <Icons variant="line" label="delete" />,
      onClick: () =>
        !appState.appRegisterStatus.zoom
          ? changeExtraModalType("REGISTER_ZOOM@DELETE")
          : changeExtraModalType(
              meetingSettingInfo.meetingInfo?.occurrenceId === "NONE"
                ? "REMOVE_SINGLE"
                : "REMOVE_RECURRENCE"
            ),
    },
  ] as {
    key: MenuListType;
    resource: string;
    icon: React.ReactNode;
    onClick?: () => void;
  }[];

  /** 함수 정리 */
  /** 밖에 눌렀을 때, 더보기 꺼짐 */
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e.target)) {
      handleTurnOffSetting();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);
  const handleTurnOffSetting = () => {
    setMeetingSettingInfo({
      x: 0,
      y: 0,
      id: "",
      meetingInfo: undefined,
    });
  };
  /** 회의 삭제 */
  const handleDeleteMeeting = async (occurenceId?: string) => {
    let resp: any = await deleteMeeting({
      meetingId: meetingSettingInfo.meetingInfo?.meetingId,
      occurenceId: occurenceId ?? "NONE",
      workgroupId: meetingSettingInfo.meetingInfo?.workgroupId,
    });
    if (resp.payload) {
      toast.toastMsg(nanoid(), t("meeting.alert.deleteSuccess"), "success");
      handleRefreshMeetingList && handleRefreshMeetingList();
    } else {
      toast.toastMsg(
        nanoid(),
        resp?.error?.message ?? t("meeting.alert.deleteFail"),
        "error"
      );
    }
    changeExtraModalType("CLOSE");
    handleTurnOffSetting();
  };
  /** Use Effect 정리 */
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });
  useEffect(() => {
    if (!inputRef.current) return;
    const observer = new ResizeObserver(() => {
      if (inputRef.current) {
        setHeight(inputRef.current.offsetHeight);
      }
    });
    observer.observe(inputRef.current);
    return () => observer.disconnect();
  }, []);
  const handleMoveToEditPage = () => {
    meetingSettingInfo.meetingInfo?.occurrenceId === "NONE"
      ? changeEditMode &&
        meetingSettingInfo.meetingInfo &&
        changeEditMode(`${meetingSettingInfo.meetingInfo?.meetingId}/NONE`)
      : changeExtraModalType("CONFIRM_RECURRENCE");
  };
  return (
    <>
      {extranModalType.startsWith("REGISTER_ZOOM") && (
        <AppRegisterModal
          open
          onClose={() => {
            changeExtraModalType("CLOSE");
            handleTurnOffSetting();
          }}
          openLinkAppModal="open_zoom"
          modalType={"CONNECT"}
          handleAfter={() =>
            extranModalType.includes("EDIT")
              ? handleMoveToEditPage()
              : changeExtraModalType(
                  meetingSettingInfo.meetingInfo?.occurrenceId === "NONE"
                    ? "REMOVE_SINGLE"
                    : "REMOVE_RECURRENCE"
                )
          }
        />
      )}
      {extranModalType !== "CLOSE" ? (
        <>
          {extranModalType === "MEETING_DETAIL" && (
            <MeetingDetailModal
              open={extranModalType === "MEETING_DETAIL"}
              onClose={() => {
                changeExtraModalType("CLOSE");
                handleTurnOffSetting();
              }}
              meetingInfo={meetingSettingInfo.meetingInfo}
            />
          )}
          {extranModalType === "CONFIRM_RECURRENCE" && (
            <Modal
              open
              onClose={() => {
                changeExtraModalType("CLOSE");
                handleTurnOffSetting();
              }}
              title={t("meeting.edit")}
              width={540}
              bodyMsg={t("meeting.recurring.edit")}
              footer={
                <Box>
                  <Button
                    label={t("meeting.recurring.editOnlyOne")}
                    btnStyle="fill"
                    type="secondary"
                    onClick={() => {
                      changeEditMode &&
                        meetingSettingInfo.meetingInfo &&
                        changeEditMode(
                          `${meetingSettingInfo.meetingInfo?.meetingId}/${meetingSettingInfo.meetingInfo.occurrenceId}`
                        );
                      changeExtraModalType("CLOSE");
                      handleTurnOffSetting();
                    }}
                  />
                  <Button
                    label={t("meeting.recurring.editAll")}
                    btnStyle="fill"
                    onClick={() => {
                      changeEditMode &&
                        meetingSettingInfo.meetingInfo &&
                        changeEditMode(
                          `${meetingSettingInfo.meetingInfo?.meetingId}/ALL`
                        );
                      changeExtraModalType("CLOSE");
                      handleTurnOffSetting();
                    }}
                  />
                </Box>
              }
            />
          )}
          {extranModalType === "REMOVE_SINGLE" && (
            <Modal
              open
              onClose={() => {
                changeExtraModalType("CLOSE");
                handleTurnOffSetting();
              }}
              title={t("meeting.deleteMeeting")}
              width={530}
              bodyMsg={t("meeting.deleteMeetingInfo")}
              footer={
                <Box>
                  <Button
                    btnStyle="fill"
                    type="secondary"
                    label={t("common.cancel")}
                    onClick={() => {
                      changeExtraModalType("CLOSE");
                      handleTurnOffSetting();
                    }}
                  />
                  <Button
                    btnStyle="fill"
                    type="error"
                    label={t("common.delete")}
                    state="hover"
                    onClick={() => {
                      handleDeleteMeeting(
                        meetingSettingInfo.meetingInfo?.occurrenceId
                      );
                      handleTurnOffSetting();
                    }}
                  />
                </Box>
              }
            />
          )}
          {extranModalType === "REMOVE_RECURRENCE" && (
            <Modal
              open
              onClose={() => {
                changeExtraModalType("CLOSE");
                handleTurnOffSetting();
              }}
              title={t("meeting.deleteMeeting")}
              width={540}
              bodyMsg={t("meeting.recurring.delete")}
              footer={
                <Box>
                  <Button
                    label={t("meeting.recurring.deleteOnlyOne")}
                    btnStyle="fill"
                    type="secondary"
                    onClick={() => {
                      handleDeleteMeeting(
                        meetingSettingInfo.meetingInfo?.occurrenceId
                      );
                      handleTurnOffSetting();
                    }}
                  />
                  <Button
                    label={t("meeting.recurring.deleteAll")}
                    btnStyle="fill"
                    type="error"
                    state="hover"
                    onClick={() => {
                      handleDeleteMeeting("ALL");
                      handleTurnOffSetting();
                    }}
                  />
                </Box>
              }
            />
          )}
          {extranModalType === "SHARE_LINK" && (
            <ShareMeetingLinkModal
              open
              onClose={() => {
                handleTurnOffSetting();
                changeExtraModalType("CLOSE");
              }}
              meetingInfo={meetingSettingInfo.meetingInfo}
            />
          )}
        </>
      ) : (
        <DropdownBody
          maxWidth={300}
          maxHeight={350}
          dropdownRef={inputRef}
          width={190}
          location={{
            x:
              meetingSettingInfo.x -
              (direction === "ltr" &&
              meetingSettingInfo.x > window.innerWidth / 2
                ? 190
                : direction !== "ltr"
                ? -240
                : 0) -
              (direction === "rtl" &&
              window.eco.companyName === "linkApp" &&
              !utilsCommon.isDemoEco()
                ? 38
                : 0) -
              (direction === "ltr"
                ? (document
                    .getElementById("sidemenuId")
                    ?.getBoundingClientRect().width ?? 240) - 245
                : 0) -
              (window.eco.serviceType === "OCI" ? 55 : 0),
            y:
              meetingSettingInfo.y -
              (meetingSettingInfo.y > window.innerHeight / 2 && height
                ? height
                : 0),
          }}
        >
          {menus
            .filter((item) =>
              utilsCommon.checkMenuAvailble(
                item.key,
                meetingSettingInfo?.meetingInfo,
                page
              )
            )
            .map((item) => (
              <DropdownItem
                height={item.key === "divider" ? undefined : 20}
                type={item.key === "deleteMeeting" ? "warning" : "default"}
                label={item.resource}
                frontIcon={item.icon}
                onClick={item.onClick}
              />
            ))}
        </DropdownBody>
      )}
    </>
  );
};
export default MeetingSettingButton;
